import React, { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { colors, fonts, spacing } from '../constants';
import { useAllQueriesPageState } from '../hooks/queries/allQueries/useAllQueriesPageState';
import { useAllQueriesPageData } from '../hooks/queries/allQueries/useAllQueriesPageData';
import { useAllQueriesPageFilters } from '../hooks/queries/allQueries/useAllQueriesPageFilters';
import { useAllQueriesPageModals } from '../hooks/queries/allQueries/useAllQueriesPageModals';
import { useAllQueriesPageOperations } from '../hooks/queries/allQueries/useAllQueriesPageOperations';
import { REGION_OPTIONS } from '../constants/allQueriesPage';
import SearchAndFilter from '../components/queries/SearchAndFilter';
import ViewQueryModal from '../components/queries/ViewQueryModal';
import EditQueryModal from '../components/queries/EditQueryModal';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import EscalateQueryDialog from '../components/queries/EscalateQueryDialog';
import RegionCardsGrid from '../components/raiseQueryPage/RegionCardsGrid';
import AllQueriesTable from '../components/allQueriesPage/AllQueriesTable';

const AllQueries = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize all hooks
  const pageState = useAllQueriesPageState();

  const { fetchQueries } = useAllQueriesPageData({
    searchQuery: pageState.searchQuery,
    filters: pageState.filters,
    setAllQueries: pageState.setAllQueries,
    setLoading: pageState.setLoading,
    setAssignedFilterOptions: pageState.setAssignedFilterOptions,
    setBackendCounts: pageState.setBackendCounts,
    setTotal: pageState.setTotal,
  });

  const {
    filteredQueries,
    paginatedQueries,
  } = useAllQueriesPageFilters({
    allQueries: pageState.allQueries,
    selectedRegionCard: pageState.selectedRegionCard,
    selectedStatusCard: pageState.selectedStatusCard,
    page: pageState.page,
    rowsPerPage: pageState.rowsPerPage,
    backendCounts: pageState.backendCounts,
    setQueries: pageState.setQueries,
    setTotal: pageState.setTotal,
    setRegionStatusSummary: pageState.setRegionStatusSummary,
    setPage: pageState.setPage,
  });

  const modals = useAllQueriesPageModals();
  const operations = useAllQueriesPageOperations(fetchQueries);

  // Update operations hook with escalatingId from modals
  operations.escalatingId = modals.escalatingId;
  operations.setEscalatingId = modals.setEscalatingId;

  // Handlers
  const handleRegionCardClick = (regionCode) => {
    if (pageState.selectedRegionCard === regionCode) {
      pageState.setSelectedRegionCard(null);
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(null);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (pageState.tableContainerRef.current) {
          pageState.tableContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleStatusCardClick = (statusLabel, regionCode, ref) => {
    const isStatusSelected = pageState.selectedRegionCard === regionCode && pageState.selectedStatusCard === statusLabel;
    if (isStatusSelected) {
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(statusLabel);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (ref?.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleView = (query) => {
    modals.handleOpenViewModal(query);
  };

  const handleEdit = (query) => {
    modals.handleOpenEditModal(query);
  };

  const handleDelete = (query) => {
    modals.handleOpenDeleteDialog(query);
  };

  const handleEscalateClick = (query) => {
    if (!query) return;
    modals.handleOpenEscalateDialog(query);
  };

  const handleConfirmEscalation = async (escalationData) => {
    const query = modals.escalateTarget;
    if (!query) return;
    try {
      await operations.performManualEscalate(query, escalationData);
    } finally {
      modals.handleCloseEscalateDialog();
    }
  };

  const handleSearchChange = (value) => {
    pageState.handleSearchChange(value);
    const newParams = { ...pageState.filters };
    if (value) {
      newParams.search = value;
    }
    setSearchParams(newParams);
  };

  const handleFilterChange = (newFilters) => {
    pageState.handleFilterChange(newFilters);
    const newParams = { ...newFilters };
    if (pageState.searchQuery) {
      newParams.search = pageState.searchQuery;
    }
    setSearchParams(newParams);
  };

  // Fetch ED users by region
  React.useEffect(() => {
    const fetchEdUsers = async () => {
      try {
        const { default: api } = await import('../services/api');
        const response = await api.get('/auth/ed-users-by-region');
        if (response.data?.success && response.data?.data?.edByRegion) {
          pageState.setEdUsersByRegion(response.data.data.edByRegion);
        }
      } catch (error) {
        // console.error('Failed to load ED users:', error);
      }
    };

    fetchEdUsers();
  }, [pageState.setEdUsersByRegion]);

  // Reset page when search or filters change
  React.useEffect(() => {
    pageState.resetPage();
  }, [pageState.searchQuery, pageState.filters, pageState.resetPage]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Typography variant="body1" sx={{
          color: '#64748b',
          fontSize: { xs: fonts.fontSizeSm, sm: fonts.fontSizeMd },
          mb: spacing.md,
          mt: 0
        }}>
          View and manage all queries in the system.
        </Typography>

        <SearchAndFilter
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          searchValue={pageState.searchQuery}
          filters={pageState.filters}
          defaultExpanded={true}
          showStatusFilter={false}
          showPriorityFilter={false}
          showProjectFilter={false}
          showLifecycleStatusFilter={false}
          showAssignedFilter={true}
          showQueryTypeFilter={false}
          assignedOptions={pageState.assignedFilterOptions}
          placeholder="Search queries by ID, title, description, or project..."
        />

        {/* Region Cards Grid */}
        <RegionCardsGrid
          visibleRegionOptions={REGION_OPTIONS}
          regionStatusSummary={pageState.regionStatusSummary}
          edUsersByRegion={pageState.edUsersByRegion}
          selectedRegionCard={pageState.selectedRegionCard}
          selectedStatusCard={pageState.selectedStatusCard}
          onRegionCardClick={handleRegionCardClick}
          onStatusCardClick={handleStatusCardClick}
          tableContainerRef={pageState.tableContainerRef}
        />

        {/* Query Table */}
        <AllQueriesTable
          loading={pageState.loading}
          total={pageState.total}
          queries={pageState.queries}
          selectedRegionCard={pageState.selectedRegionCard}
          onView={handleView}
          page={pageState.page}
          rowsPerPage={pageState.rowsPerPage}
          handleChangePage={pageState.handleChangePage}
          handleChangeRowsPerPage={pageState.handleChangeRowsPerPage}
          tableContainerRef={pageState.tableContainerRef}
        />

        {/* Modals */}
        <EscalateQueryDialog
          open={modals.escalateDialogOpen}
          query={modals.escalateTarget}
          submitting={operations.escalatingId === modals.escalateTarget?._id}
          onClose={modals.handleCloseEscalateDialog}
          onSubmit={handleConfirmEscalation}
        />
        <ViewQueryModal
          open={modals.viewModalOpen}
          onClose={modals.handleCloseViewModal}
          queryId={modals.selectedQuery?._id}
          onDelegated={fetchQueries}
          onEdit={(query) => {
            modals.handleCloseViewModal();
            handleEdit(query);
          }}
          onDelete={(query) => {
            modals.handleCloseViewModal();
            handleDelete(query);
          }}
        />
        <EditQueryModal
          open={modals.editModalOpen}
          onClose={modals.handleCloseEditModal}
          query={modals.selectedQuery}
          onSuccess={() => {
            fetchQueries();
          }}
        />
        <ConfirmationDialog
          open={modals.deleteDialogOpen}
          onClose={modals.handleCloseDeleteDialog}
          onConfirm={() => operations.confirmDelete(modals.selectedQuery)}
          title="Delete Query"
          message={`Are you sure you want to delete query "${modals.selectedQuery?.queryId}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
      </Box>
    </Box>
  );
};

export default AllQueries;
